import { createTheme } from '@material-ui/core/styles'

// Global cyberpunk theme for Material-UI
const cyberpunkTheme = createTheme({
  palette: {
    type: 'dark',
    primary: {
      main: '#ff00ff', // Bright magenta
      light: '#ff66ff',
      dark: '#cc00cc',
      contrastText: '#ffffff',
    },
    secondary: {
      main: '#00ffff', // Cyan
      light: '#66ffff',
      dark: '#00cccc',
      contrastText: '#000000',
    },
    error: {
      main: '#ff3366', // Glitch red
    },
    warning: {
      main: '#ffff00', // Electric yellow
    },
    info: {
      main: '#00ffff', // Cyan
    },
    success: {
      main: '#00ff00', // Matrix green
    },
    background: {
      default: '#0a0a0a',
      paper: 'rgba(12, 12, 12, 0.98)',
    },
    text: {
      primary: '#ffffff',
      secondary: '#00ffff',
    },
  },
  typography: {
    fontFamily: '"Orbitron", "Share Tech Mono", monospace',
    h1: {
      fontFamily: '"Orbitron", monospace',
      fontWeight: 900,
      fontSize: '3.5rem',
      letterSpacing: '0.1em',
      textTransform: 'uppercase',
    },
    h2: {
      fontFamily: '"Orbitron", monospace',
      fontWeight: 700,
      fontSize: '2.5rem',
      letterSpacing: '0.08em',
    },
    h3: {
      fontFamily: '"Orbitron", monospace',
      fontWeight: 700,
      fontSize: '2rem',
      letterSpacing: '0.06em',
    },
    h4: {
      fontFamily: '"Orbitron", monospace',
      fontWeight: 600,
      fontSize: '1.5rem',
      letterSpacing: '0.04em',
    },
    h5: {
      fontFamily: '"Orbitron", monospace',
      fontWeight: 600,
      fontSize: '1.25rem',
      letterSpacing: '0.02em',
    },
    h6: {
      fontFamily: '"Orbitron", monospace',
      fontWeight: 600,
      fontSize: '1rem',
      letterSpacing: '0.02em',
    },
    body1: {
      fontFamily: '"Share Tech Mono", monospace',
      fontSize: '1rem',
      lineHeight: 1.6,
    },
    body2: {
      fontFamily: '"Share Tech Mono", monospace',
      fontSize: '0.875rem',
      lineHeight: 1.5,
    },
    button: {
      fontFamily: '"Orbitron", monospace',
      fontWeight: 700,
      textTransform: 'uppercase',
      letterSpacing: '0.1em',
    },
  },
  overrides: {
    // Global component overrides
    MuiCssBaseline: {
      '@global': {
        body: {
          background: '#080808',
          backgroundAttachment: 'fixed',
          '&::before': {
            content: '""',
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundImage: `
              repeating-linear-gradient(
                0deg,
                transparent,
                transparent 29px,
                rgba(255, 255, 255, 0.03) 29px,
                rgba(255, 255, 255, 0.03) 30px
              ),
              repeating-linear-gradient(
                90deg,
                transparent,
                transparent 29px,
                rgba(255, 255, 255, 0.03) 29px,
                rgba(255, 255, 255, 0.03) 30px
              )
            `,
            backgroundSize: '30px 30px',
            pointerEvents: 'none',
            zIndex: 9000,
          },
        },
        '*::-webkit-scrollbar': {
          width: '8px',
        },
        '*::-webkit-scrollbar-track': {
          background: 'rgba(20, 20, 20, 0.5)',
        },
        '*::-webkit-scrollbar-thumb': {
          background: 'linear-gradient(45deg, #ff00ff, #00ffff)',
          borderRadius: '4px',
          '&:hover': {
            background: 'linear-gradient(45deg, #ff66ff, #66ffff)',
          },
        },
      },
    },
    MuiButton: {
      root: {
        background: 'linear-gradient(45deg, transparent, rgba(255, 0, 255, 0.1), transparent)',
        border: '2px solid #ff00ff',
        color: '#ff00ff',
        padding: '12px 24px',
        position: 'relative',
        overflow: 'hidden',
        transition: 'all 0.3s ease',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: '-100%',
          width: '100%',
          height: '100%',
          background: 'linear-gradient(90deg, transparent, rgba(255, 0, 255, 0.2), transparent)',
          transition: 'left 0.5s ease',
        },
        '&:hover': {
          boxShadow: '0 0 20px rgba(255, 0, 255, 0.5), inset 0 0 20px rgba(255, 0, 255, 0.1)',
          transform: 'translateY(-2px)',
          '&::before': {
            left: '100%',
          },
        },
      },
      containedPrimary: {
        background: 'linear-gradient(45deg, #ff00ff, #ff66ff)',
        color: '#ffffff',
        border: 'none',
        '&:hover': {
          background: 'linear-gradient(45deg, #ff66ff, #ff00ff)',
          boxShadow: '0 0 25px rgba(255, 0, 255, 0.6)',
        },
      },
      containedSecondary: {
        background: 'linear-gradient(45deg, #00ffff, #66ffff)',
        color: '#000000',
        border: 'none',
        '&:hover': {
          background: 'linear-gradient(45deg, #66ffff, #00ffff)',
          boxShadow: '0 0 25px rgba(0, 255, 255, 0.6)',
        },
      },
    },
    MuiPaper: {
      root: {
        background: 'rgba(12, 12, 12, 0.95)',
        border: '1px solid rgba(255, 0, 255, 0.3)',
        backdropFilter: 'blur(10px)',
        boxShadow: '0 0 20px rgba(255, 0, 255, 0.1)',
      },
    },
    MuiCard: {
      root: {
        background: 'rgba(12, 12, 12, 0.95)',
        border: '1px solid rgba(255, 0, 255, 0.3)',
        backdropFilter: 'blur(10px)',
        boxShadow: '0 0 20px rgba(255, 0, 255, 0.1)',
        transition: 'all 0.3s ease',
        '&:hover': {
          boxShadow: '0 0 30px rgba(255, 0, 255, 0.3)',
          transform: 'translateY(-5px)',
        },
      },
    },
    MuiTextField: {
      root: {
        '& .MuiOutlinedInput-root': {
          '& fieldset': {
            borderColor: '#ff00ff',
          },
          '&:hover fieldset': {
            borderColor: '#ff66ff',
            boxShadow: '0 0 10px rgba(255, 0, 255, 0.3)',
          },
          '&.Mui-focused fieldset': {
            borderColor: '#00ffff',
            boxShadow: '0 0 15px rgba(0, 255, 255, 0.5)',
          },
        },
        '& .MuiInputLabel-root': {
          color: '#00ffff',
          fontFamily: '"Share Tech Mono", monospace',
        },
        '& .MuiOutlinedInput-input': {
          color: '#ffffff',
          fontFamily: '"Share Tech Mono", monospace',
        },
      },
    },
    MuiAppBar: {
      root: {
        background: 'rgba(10, 10, 10, 0.95)',
        backdropFilter: 'blur(15px)',
        borderBottom: '1px solid rgba(255, 0, 255, 0.3)',
        boxShadow: '0 0 20px rgba(255, 0, 255, 0.2)',
      },
    },
    MuiDrawer: {
      paper: {
        background: 'rgba(10, 10, 10, 0.98)',
        backdropFilter: 'blur(20px)',
        border: '1px solid rgba(255, 0, 255, 0.3)',
        boxShadow: '0 0 30px rgba(255, 0, 255, 0.3)',
      },
    },
    MuiListItem: {
      root: {
        color: '#00ffff',
        fontFamily: '"Share Tech Mono", monospace',
        '&:hover': {
          background: 'rgba(255, 0, 255, 0.1)',
          boxShadow: 'inset 0 0 10px rgba(255, 0, 255, 0.2)',
        },
        '&.Mui-selected': {
          background: 'rgba(0, 255, 255, 0.1)',
          color: '#ff00ff',
          '&:hover': {
            background: 'rgba(0, 255, 255, 0.2)',
          },
        },
      },
    },
    MuiMenuItem: {
      root: {
        color: '#00ffff',
        fontFamily: '"Share Tech Mono", monospace',
        fontSize: '0.9rem',
        '&:hover': {
          background: 'rgba(255, 0, 255, 0.1)',
          color: '#ff00ff',
        },
      },
    },
  },
})

// CSS-in-JS styles for components that don't use Material-UI
export const globalCyberpunkStyles = {
  // Cyberpunk animations
  '@keyframes cyberpunkGlow': {
    '0%, 100%': {
      textShadow: '0 0 5px currentColor, 0 0 10px currentColor, 0 0 15px currentColor',
    },
    '50%': {
      textShadow: '0 0 10px currentColor, 0 0 20px currentColor, 0 0 30px currentColor',
    },
  },
  '@keyframes cyberpunkPulse': {
    '0%, 100%': {
      opacity: 1,
      transform: 'scale(1)',
    },
    '50%': {
      opacity: 0.8,
      transform: 'scale(1.05)',
    },
  },
  '@keyframes cyberpunkScan': {
    '0%': {
      transform: 'translateY(-100%)',
    },
    '100%': {
      transform: 'translateY(100vh)',
    },
  },
  
  // Global cyberpunk classes
  cyberpunkText: {
    fontFamily: '"Share Tech Mono", monospace',
    color: '#00ffff',
    textShadow: '0 0 5px rgba(0, 255, 255, 0.5)',
  },
  cyberpunkTitle: {
    fontFamily: '"Orbitron", monospace',
    fontWeight: 700,
    color: '#ff00ff',
    textShadow: '0 0 10px rgba(255, 0, 255, 0.5)',
    textTransform: 'uppercase',
    letterSpacing: '0.1em',
  },
  cyberpunkBorder: {
    border: '1px solid #ff00ff',
    boxShadow: '0 0 10px rgba(255, 0, 255, 0.3)',
  },
  cyberpunkGlow: {
    animation: 'cyberpunkGlow 2s ease-in-out infinite',
  },
  cyberpunkPulse: {
    animation: 'cyberpunkPulse 3s ease-in-out infinite',
  },
}

export default cyberpunkTheme
